import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';

const translations = {
  es: {
    dynamicTitle: 'Una plataforma útil para',
    rotatingTexts: ['el empleado', 'el líder', 'el Director de Talento', 'todos'],
    buttons: ['Líder', 'Empleado', 'Director de Talento'],
  },
  en: {
    dynamicTitle: 'A useful platform for',
    rotatingTexts: ['the employee', 'the leader', 'the Talent Director', 'everyone'],
    buttons: ['Leader', 'Employee', 'Talent Director'],
  },
};

function Product({ language }) {
  const { dynamicTitle, rotatingTexts, buttons } = translations[language];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(buttons[0]);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 600);

  // Detectar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Solo si NO es pantalla pequeña se ejecuta la animación del texto
  useEffect(() => {
    if (!isSmallScreen) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % rotatingTexts.length);
      }, rotatingTexts[currentIndex] === 'todos' || rotatingTexts[currentIndex] === 'everyone' ? 6000 : 4000);

      return () => clearInterval(interval);
    }
  }, [currentIndex, rotatingTexts, isSmallScreen]);

  useEffect(() => {
    setActiveTab(buttons[0]);
  }, [language, buttons]);

  const tabTitles = {
    Líder: 'Tu Aliado en Gestión de Talento',
    Empleado: 'Tu Equipo en Modo Superhéroe',
    'Director de Talento': 'Todo el Talento Bajo Control',
    Leader: 'Your Partner in Talent Management',
    Employee: 'Your Team in Superhero Mode',
    'Talent Director': 'All Talent Under Control',
  };

  const tabContent = {
    // Employee / Empleado
    Empleado: [
      'Identificar sus propias oportunidades de crecimiento',
      'Aprender e implementar nuevas acciones que se convierten en hábitos',
      'Cocrear y asumir la responsabilidad de sus planes de desarrollo',
    ],
    Employee: [
      'Identify their own growing opportunities',
      'Learn & implement new actions that became habits',
      'Cocreate and own their development plans',
    ],
    // Leader / Líder
    Líder: [
      'Analizar el equipo para detectar las oportunidades de desarrollo.',
      'Asegurar la continuidad del negocio con alertas de riesgo de rotación.',
      'Herramientas de talento AI que te proponen el siguiente paso para cada talento.',
    ],
    Leader: [
      'Analyse the team to detect talent development focus.',
      'Ensure business continuity with turnover risk warnings.',
      'AI-Talent tool that proposes next step for each talent.',
    ],
    // Talent Director / Director de Talento
    'Director de Talento': [
      'Anticipar y mitigar los riesgos de rotación en el negocio.',
      'Asegurar que las estrategias de sucesión y sostenibilidad empresarial estén implementadas.',
      'Crear campañas de talento para detectar oportunidades de crecimiento.',
    ],
    'Talent Director': [
      'Anticipate and mitigate turnover business risks.',
      'Ensure succession and business sustainability strategies are placed.',
      'Create talent campaigns to grow.',
    ],
  };

  return (
    <section id="product" style={styles.section}>
      <div style={styles.contentBox}>
        <h1 style={styles.dynamicTitle}>
          {dynamicTitle}
          {isSmallScreen ? (
            <span style={styles.rotatingText}>
              {language === 'es' ? 'todos' : 'everyone'}
            </span>
          ) : (
            <AnimatePresence mode="wait">
              <motion.span
                key={currentIndex}
                style={styles.rotatingText}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.5 }}
              >
                {rotatingTexts[currentIndex]}
              </motion.span>
            </AnimatePresence>
          )}
        </h1>
        <div style={styles.buttonContainer}>
          {buttons.map((button) => (
            <button
              key={button}
              onClick={() => setActiveTab(button)}
              style={activeTab === button ? { ...styles.activeButton } : { ...styles.button }}
            >
              {button}
            </button>
          ))}
        </div>
        <div style={styles.tabContent}>
          <div
            style={
              activeTab === 'Empleado' || activeTab === 'Employee'
                ? styles.imageContainerEmployee
                : activeTab === 'Líder' || activeTab === 'Leader'
                ? styles.imageContainerLeader
                : styles.imageContainerPlaceholder
            }
          ></div>
          <div style={styles.textContentCentered}>
            <h2 style={styles.title}>{tabTitles[activeTab]}</h2>
            <ul style={styles.list}>
              {tabContent[activeTab].map((item, index) => (
                <li key={index} style={styles.listItem}>
                  <FontAwesomeIcon icon={faCircleCheck} style={styles.icon} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

const styles = {
  section: {
    width: '100%',
    minHeight: '70vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    margin: 0,
    padding: '10px',
    boxSizing: 'border-box',
    position: 'relative',
  },
  contentBox: {
    width: '100%',
    maxWidth: 'calc(100% - 40px)',
    border: '1px solid #ddd',
    borderRadius: '20px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    padding: '40px',
    boxSizing: 'border-box',
    position: 'relative',
  },
  dynamicTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '20px',
    textAlign: 'left',
  },
  rotatingText: {
    marginLeft: '8px',
    color: '#ff2300',
    fontWeight: '700',
  },
  buttonContainer: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    marginTop: '20px',
  },
  button: {
    padding: '10px 20px',
    borderRadius: '20px',
    border: 'none',
    backgroundColor: '#f0f0f0',
    fontSize: '1rem',
    fontWeight: '400',
    cursor: 'pointer',
    minWidth: '120px',
    textAlign: 'center',
    transition: 'background-color 0.3s, color 0.3s',
  },
  activeButton: {
    padding: '10px 20px',
    borderRadius: '20px',
    border: 'none',
    backgroundColor: '#d3d3d3',
    fontSize: '1rem',
    fontWeight: '400',
    cursor: 'pointer',
    color: '#333',
    minWidth: '120px',
    textAlign: 'center',
    transition: 'background-color 0.3s, color 0.3s',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '30px',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  imageContainerPlaceholder: {
    flex: '1 1 300px',
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundColor: '#EAEBED',
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
  imageContainerEmployee: {
    flex: '1 1 300px',
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundImage: 'url(/screenshots/employeeproduct.png)',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  imageContainerLeader: {
    flex: '1 1 300px',
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundImage: 'url(/screenshots/liderproduct.png)',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  textContentCentered: {
    flex: '1 1 300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    textAlign: 'left',
    padding: '20px',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '600px',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    marginBottom: '10px',
    textAlign: 'left',
  },
  list: {
    fontSize: '1.1rem',
    listStyle: 'none',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    lineHeight: '1.6',
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '8px',
    fontWeight: '300',
  },
  icon: {
    marginRight: '8px',
    color: '#ff2300',
    minWidth: '20px',
    marginTop: '4px',
  },
};

export default Product;
